"use client";

import useGetCMS from "@/hooks/use-get-cms";
import useLocaleLegacy from "@/hooks/use-locale-legacy";
import locale from "@/locales/sales.json";
import { MailOpen } from "lucide-react";
import Image from "next/image";
import { useParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import VirtualTour from "../homepage/virtual-tour";
import { cacheRevalidateDuration } from "@/app/models/cache";
import useSWRMutation from "swr/mutation";
import { fetcher } from "@/lib/fetcher";

export default function SalesDetail() {
  const { t } = useLocaleLegacy(locale);

  const { slug } = useParams();

  const [salesData, setSalesData] = useState(undefined);
  const name = useRef<HTMLInputElement>(null);
  const phone = useRef<HTMLInputElement>(null);

  const { trigger: submitSalesForm } = useSWRMutation(
    "/nxapi/sales-in-touches",
    async function (url, { arg }: { arg: Record<string, any> }) {
      return fetcher(url, {
        method: "POST",
        body: JSON.stringify(arg),
        headers: {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_API_TOKEN}`,
          "Content-Type": "application/json",
        },
      });
    }
  );

  const { data, loading } = useGetCMS({
    key: "sales",
    collection: "sales",
    config: {
      next: {
        revalidate: cacheRevalidateDuration()
      }
    }
  });

  const { data: homepage, loading: loadingGetVirtual } = useGetCMS({
    key: "homepage",
    collection: "homepage",
    config : {
      next : {
        revalidate: cacheRevalidateDuration()
      }
    }
  });

  const virtualHomepage = useMemo(() => {
    if (!homepage) return;

    const templates = homepage.data.content;

    const virtual = templates.find(
      (template: any) => template.__component === "homepage.virtual-tour"
    );

    return virtual;
  }, [homepage]);

  useEffect(() => {
    if (!data?.data) return;

    const _saleData = data.data.find((data) => data.slug === slug);
    setSalesData(_saleData);
    return;
  }, [data?.data]);

  const sendWa = () => {
    submitSalesForm({
      name: name.current?.value || "",
      phone_number: phone.current?.value || "",
      sales: salesData.email,
    }).catch(console.error);
    window.open(
      `https://wa.me/${salesData.phone_number}?text=Hai,%20saya%20dapat%20info%20dari%20website%20dan%20tertarik%20dengan%20unit%20di%20The%20Elements,%20mohon%20informasinya%20:)`
    );
  };

  if (loading || !salesData) return <></>;

  return (
    <>
      <div className="px-9 xl:pl-60 xl:pr-48 pt-10 xl:pt-24 relative bg-linen">
        <img
          src="/homepage/stempel.svg"
          alt="logo"
          className="absolute -top-1/12 xl:-top-2/12 -left-1/12 w-[223px] xl:w-auto h-[223px] xl:h-auto"
        />

        <div className="flex z-10 flex-col xl:flex-row">
          <div className="flex-none z-[1] flex flex-col items-center xl:items-start">
            <img
              src={`${process.env.NEXT_PUBLIC_BASE_STATIC_URL}${salesData.image.url}`}
              alt=""
              className="object-cover rounded-[20px] w-[145px] xl:w-full h-[122px] xl:h-[500px]"
              height={500}
            />
            {salesData.verified && (
              <div className="flex items-center mt-3.5 xl:mt-7">
                {/* <div className="w-5 xl:w-10 h-5 xl:h-10 rounded-full bg-gold  grid place-items-center"> */}
                <img
                  src="/tiktok-verified.svg"
                  className="w-3 xl:w-12 h-3 xl:h-12"
                />
                {/* <Check color="white" className="w-3 xl:w-6  h-3 xl:h-6" /> */}
                {/* </div> */}

                <p className="text-[14px] xl:text-4xl ml-1 xl:ml-3 text-coffebrown font-medium">
                  Verified
                </p>
              </div>
            )}
          </div>

          <div className="flex-1 mt-3 xl:mt-0 xl:ml-10">
            <p className="text-darkgold text-center xl:text-left text-[14px] xl:text-2xl">
              {t("welcome")}
            </p>
            <p className="text-darkgold text-center xl:text-left font-medium text-2xl xl:text-5xl mb-1 xl:mb-4 font-pluto">
              {salesData.name}
            </p>
            <p className="text-darkgold text-center xl:text-left text-[14px] xl:text-2xl">
              {t("marketing_in_house")}
            </p>

            <div className="flex mt-6 xl:mt-16">
              <div className="w-5 xl:w-12 h-5 xl:h-12 rounded-full bg-gold grid place-items-center">
                <MailOpen color="white" className="w-3 xl:w-6 h-3 xl:h-6" />
              </div>
              <div className="ml-2.5">
                <div className="rounded-[49px] xl:text-3xl px-3 xl:px-5 xl:py-1 w-fit bg-gold text-white font-medium mb-2 xl:mb-4 grid place-items-center h-5 xl:h-12">
                  {t("email")}
                </div>
                <p className="text-brown text-[14px] xl:text-3xl break-words">
                  {salesData.email}
                </p>
              </div>
            </div>

            <div className="flex mt-6">
              <div className="w-5 xl:w-12 h-5 xl:h-12 shrink-0 rounded-full bg-gold grid place-items-center">
                <img
                  src="/Home.svg"
                  className="w-4 xl:w-8 h-4 xl:h-8 object-cover"
                  alt="home"
                />
              </div>
              <div className="ml-2.5">
                <div className="rounded-[49px] xl:text-3xl px-3 xl:px-5 xl:py-1 w-fit bg-gold text-white font-medium mb-2 xl:mb-4 grid place-items-center h-5 xl:h-12">
                  {t("whatsapp")}
                </div>
                <p className="text-brown text-[14px] xl:text-3xl whitespace-normal">
                  {salesData.phone_number}
                </p>
              </div>
            </div>
          </div>
        </div>

        <hr className="border-gold border mt-10 xl:mt-12 mb-7 xl:mb-16" />

        <p
          className="text-darkgold xl:text-5xl text-center mb-5 xl:mb-14  font-pluto"
          dangerouslySetInnerHTML={{ __html: t("submission_title") }}
        ></p>

        <div className="flex gap-x-6 flex-col xl:flex-row gap-y-5">
          <input
            ref={name}
            className="rounded-[3.3rem] flex-1 text-darkgold placeholder:text-darkgold border-2 xl:border border-gold py-3 px-5 text-[10px] xl:text-2xl"
            type="text"
            placeholder={t("submission_input_1_placeholder")}
          />
          <input
            ref={phone}
            className="rounded-[3.3rem] flex-1 text-darkgold placeholder:text-darkgold border-2 xl:border border-gold py-3 px-5 text-[10px] xl:text-2xl"
            type="tel"
            placeholder={t("submission_input_2_placeholder")}
          />
        </div>

        <div className="flex justify-center mt-9 pb-10 xl:pb-28">
          <button
            onClick={sendWa}
            className="inline-flex items-center py-2 xl:py-3.5 rounded-[3.25rem] bg-gold px-5 cursor-pointer"
          >
            <Image
              src="/wa-white.svg"
              alt="whatsapp"
              width={32}
              height={32}
              className="w-4 xl:w-8 h-4 xl:h-8"
            />
            <span className="ml-2 text-white uppercase font-medium text-[10px] xl:text-2xl font-pluto">
              {t("get_in_touch")}
            </span>
          </button>
        </div>
      </div>

      {!loadingGetVirtual && (
        <VirtualTour
          description={virtualHomepage?.description}
          title={virtualHomepage?.title}
          types={virtualHomepage?.types}
        />
      )}
    </>
  );
}
